<template>
  <div data-testid="yard-asset-visit-timeline">
    <template v-for="group in groupedEvents" :key="group[0]">
      <p class="typography-caption-bold text--color-text-tertiary mb-1">{{ group[0] }}</p>

      <v-timeline
        class="asset-visit-events-timeline"
        direction="vertical"
        align="center"
        side="end"
        line-inset="8">
        <v-timeline-item
          v-for="event in group[1]"
          :key="event.id"
          density="compact"
          dot-color="grey"
          size="6"
          data-testid="yard-asset-visit-timeline-event">
          <component :is="getEventComponent(event)" :timezone="warehouseTimezone" :event="event" />
        </v-timeline-item>
      </v-timeline>
    </template>
  </div>
</template>
<script>
import { defineComponent, computed } from 'vue';
import { orderBy } from 'lodash';
import { useNovaCore, useSettings, useOrg } from '@/composables';
import useYard from '@/modules/yard/composables/useYard';
import AssetVisitGenericEvent from './AssetVisitGenericEvent.vue';
import AssetVisitAppointmentLinkedEvent from './AssetVisitAppointmentLinkedEvent.vue';
import AssetVisitArrivedEvent from './AssetVisitArrivedEvent.vue';
import AssetVisitDockedEvent from './AssetVisitDockedEvent.vue';
import AssetVisitParkedEvent from './AssetVisitParkedEvent.vue';
import AssetVisitUnassignedEvent from './AssetVisitUnassignedEvent.vue';
import AssetVisitDroppedEvent from './AssetVisitDroppedEvent.vue';
import AssetVisitHookedEvent from './AssetVisitHookedEvent.vue';
import AssetVisitStatusUpdatedEvent from './AssetVisitStatusUpdatedEvent.vue';
import AssetVisitCanceledEvent from './AssetVisitCanceledEvent.vue';
import AssetVisitAssignedEvent from './AssetVisitAssignedEvent.vue';
import AssetVisitAppointmentUnlinkedEvent from './AssetVisitAppointmentUnlinkedEvent.vue';
import { useI18n } from 'vue-i18n';

const MAP_EVENT_TYPE_TO_COMPONENT = {
  'Appointment Linked': AssetVisitAppointmentLinkedEvent,
  'Appointment Unlinked': AssetVisitAppointmentUnlinkedEvent,
  Arrived: AssetVisitArrivedEvent,
  Assigned: AssetVisitAssignedEvent,
  Docked: AssetVisitDockedEvent,
  Parked: AssetVisitParkedEvent,
  Unassigned: AssetVisitUnassignedEvent,
  Dropped: AssetVisitDroppedEvent,
  Hooked: AssetVisitHookedEvent,
  'Status Updated': AssetVisitStatusUpdatedEvent,
  Canceled: AssetVisitCanceledEvent
};

export default defineComponent({
  name: 'YardViewAssetVisitTimeline',
  components: {
    AssetVisitGenericEvent
  },
  props: {
    assetVisitEvents: {
      type: Array,
      required: true
    }
  },
  setup(props) {
    const { locale } = useI18n();
    const novaCore = useNovaCore();
    const { settingsGettersRef } = useSettings();
    const org = useOrg();
    const yardStore = useYard();

    const getEventComponent = event => {
      if (!event?.eventType) {
        return AssetVisitGenericEvent;
      }

      return MAP_EVENT_TYPE_TO_COMPONENT[event.eventType] ?? AssetVisitGenericEvent;
    };

    const groupedEvents = computed(() => {
      if (!props.assetVisitEvents || props.assetVisitEvents.length === 0) {
        return [];
      }

      // Sort events by createDateTime descending (most recent first)
      const sortedEvents = orderBy(props.assetVisitEvents, ['createDateTime'], ['desc']);

      const groupedByDate = {};
      sortedEvents.forEach(event => {
        const date = novaCore.formatDateTimeWithMilitarySupport(
          event.createDateTime,
          'UTC',
          novaCore.LuxonDateTimeFormats.DateWithAbbreviatedMonth,
          settingsGettersRef.value.isMilitaryTimeEnabled(org),
          novaCore.LuxonDateTimeFormats.DateWithAbbreviatedMonth,
          locale.value
        );
        groupedByDate[date] = [...(groupedByDate[date] || []), event];
      });

      return Object.entries(groupedByDate);
    });

    return {
      getEventComponent,
      groupedEvents,
      warehouseTimezone: yardStore.selectedWarehouseRef.value?.timezone ?? null
    };
  }
});
</script>

<style lang="scss">
.v-timeline.asset-visit-events-timeline {
  --v-timeline-line-thickness: 1px;

  row-gap: $spacing-4 !important;
  justify-content: flex-start;
  padding-block-end: $spacing-8;

  .v-timeline-item__body {
    padding: 0 !important;
  }

  .v-card .v-card-title,
  .v-card .v-card-text {
    padding: 0 $spacing-2 !important;
  }
}
</style>
